/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable max-statements */
import React, { useEffect, useState } from 'react';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler,
	ScriptableContext
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useDispatch } from 'react-redux';
import { useQuery } from 'react-query';
import Scrollbars from 'react-custom-scrollbars-2';
import moment from 'moment';

import counter from '../../../../assets/images/analytics/counter.png';
import cycle from '../../../../assets/images/analytics/cycle.png';
import users from '../../../../assets/images/analytics/users.png';
import watch from '../../../../assets/images/analytics/watch.png';
import { axiosInstance } from '../../../../config/axiosInstance';
import { globalEnv } from '../../../../config/baseConfig';
import { useTypedSelector } from '../../../../hooks/useTypeSelector';
import { hideSearchbarAction } from '../../../../redux/actions/searchBarAction';
import {
	setSelectedSection,
	setTab
} from '../../../../redux/actions/sideBarAction';
import LazyImage from '../../../Utils/Components/imageComponent';
import { DataNotFoundAnimation } from '../../../Utils/ErrorAnimations/dataNotFoundAnim';
import LoadingAnimComponent from '../../../Utils/LoadingAnimations/loadingAnimation';
import { useParams } from 'react-router-dom';

export default function OutreachAnalytics() {
	const { campaignId } = useParams();
	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});
	useEffect(() => {
		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);

	const today = new Date();
	const [startDate, setStartDate] = useState(
		moment(today).subtract(7, 'days').format('YYYY-MM-DD')
	);
	const [endDate] = useState(moment(today).format('YYYY-MM-DD'));

	const [period, setPeriod] = useState('week');
	const [labels, setLabels] = useState<string[]>([]);
	const [showMore, setShowMore] = useState(false);

	const [totalBounceCount, setTotalBounceCount] = useState(0);
	const [totalDeliveryCount, setTotalDeliveryCount] = useState(0);
	const [totalOpenCount, setTotalOpenCount] = useState(0);
	const [totalComplaintCount, setTotalComplaintCount] = useState(0);

	const dispatch = useDispatch();
	dispatch(setSelectedSection('Global Stats'));
	dispatch(hideSearchbarAction());
	dispatch(setTab('outreach'));

	type DailyStatsType = {
		deliveryCount: number;
		bounceCount: number;
		complaintCount: number;
		openCount: number;
		createdAt?: Date;
	};
	const [periodicStats, setPeriodicStats] = useState<DailyStatsType[]>([]);
	const [latestStats, setLatestStats] = useState<DailyStatsType>({
		deliveryCount: 0,
		bounceCount: 0,
		complaintCount: 0,
		openCount: 0
	});

	useEffect(() => {
		switch (period) {
			case 'month': {
				const newStartDate = moment(today).subtract(1, 'months');
				setStartDate(newStartDate.format('YYYY-MM-DD'));
				break;
			}
			case 'sixMonths': {
				const newStartDate = moment(today).subtract(6, 'months');
				setStartDate(newStartDate.format('YYYY-MM-DD'));
				break;
			}
			case 'year': {
				const newStartDate = moment(today).subtract(1, 'years');
				setStartDate(newStartDate.format('YYYY-MM-DD'));
				break;
			}
			default: {
				const newStartDate = moment(today).subtract(7, 'days');
				setStartDate(newStartDate.format('YYYY-MM-DD'));
				break;
			}
		}
	}, [period]);

	async function fetchAnalyticsData() {
		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};

		let endpoint = 'emailing/delivery-stats';

		if (campaignId != null) endpoint = `campaign/${campaignId}/analytics`;

		const { data } = await axiosInstance.get(
			`${globalEnv.BASE_URL}/outreach/${endpoint}?startDate=${startDate}&endDate=${endDate}`,
			config
		);
		return data;
	}

	const { isLoading, isFetching, isError } = useQuery(
		['analytics', startDate, endDate],
		() => fetchAnalyticsData(),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 5000,

			onSuccess: (data) => {
				setPeriodicStats(data.periodicStats);
				setLatestStats(data.latestStats);

				setTotalBounceCount(
					data.periodicStats.reduce((acc: number, item: any) => {
						return acc + item.bounceCount;
					}, 0)
				);
				setTotalDeliveryCount(
					data.periodicStats.reduce((acc: number, item: any) => {
						return acc + item.deliveryCount;
					}, 0)
				);
				setTotalOpenCount(
					data.periodicStats.reduce((acc: number, item: any) => {
						return acc + item.openCount;
					}, 0)
				);
				setTotalComplaintCount(
					data.periodicStats.reduce((acc: number, item: any) => {
						return acc + item.complaintCount;
					}, 0)
				);

				const dates: any = [];
				data.periodicStats.forEach((item: any) => {
					let date;

					switch (period) {
						case 'month': {
							date = moment(item.createdAt).format('MM/DD');
							break;
						}
						case 'sixMonths': {
							date = moment(item.createdAt).format('MM/DD/YYYY');
							break;
						}
						case 'year': {
							date = moment(item.createdAt).format('MM/DD/YYYY');
							break;
						}
						default: {
							date = moment(item.createdAt).format('MM/DD');
							break;
						}
					}

					dates.push(date);
				});

				setLabels(dates);
			}
		}
	);

	ChartJS.register(
		CategoryScale,
		LinearScale,
		PointElement,
		LineElement,
		Filler,
		Title,
		Tooltip,
		Legend
	);

	const getDeliveryCount = () => {
		const data = periodicStats.map((item) => item.deliveryCount);
		data.push(latestStats.deliveryCount);
		return data;
	};
	const getBounceCount = () => {
		const data = periodicStats.map((item) => item.bounceCount);
		data.push(latestStats.bounceCount);
		return data;
	};
	const getComplaintCount = () => {
		const data = periodicStats.map((item) => item.complaintCount);
		data.push(latestStats.complaintCount);
		return data;
	};
	const getOpenCount = () => {
		const data = periodicStats.map((item) => item.openCount);
		data.push(latestStats.openCount);
		return data;
	};

	const deliveryCountOptions = {
		responsive: true,
		plugins: {
			legend: {
				display: false
			},
			title: {
				display: true,
				text: `Mail Delivery Count - (Total per period: ${totalDeliveryCount})`
			}
		}
	};

	const deliveryCountData = {
		labels,
		datasets: [
			{
				fill: true,
				lineTension: 0.3,
				data: getDeliveryCount(),
				borderColor: 'rgb(33, 202, 155)',
				backgroundColor: (context: ScriptableContext<'line'>) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 260);
					gradient.addColorStop(0, 'rgba(33, 202, 155,0.6)');
					gradient.addColorStop(1, 'rgba(33, 202, 155,0.03)');
					return gradient;
				}
			}
		]
	};

	const bounceCountOptions = {
		responsive: true,
		plugins: {
			legend: {
				display: false
			},
			title: {
				display: true,
				text: `Mail Bounce Count - (Total per period: ${totalBounceCount})`
			}
		}
	};

	const bounceCountData = {
		labels,
		datasets: [
			{
				lineTension: 0.3,
				fill: true,
				data: getBounceCount(),
				borderColor: 'rgb(53, 102, 235)',
				backgroundColor: (context: ScriptableContext<'line'>) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 260);
					gradient.addColorStop(0, 'rgba(53, 102, 235,0.6)');
					gradient.addColorStop(1, 'rgba(53, 102, 235,0.03)');
					return gradient;
				}
			}
		]
	};

	const complaintCountOptions = {
		responsive: true,
		plugins: {
			legend: {
				display: false
			},
			title: {
				display: true,
				text: `Mail Complaint Count - (Total per period: ${totalComplaintCount})`
			}
		}
	};

	const complaintCountData = {
		labels,
		datasets: [
			{
				lineTension: 0.3,
				fill: true,
				data: getComplaintCount(),
				borderColor: 'rgb(255, 49, 82)',
				backgroundColor: (context: ScriptableContext<'line'>) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 260);
					gradient.addColorStop(0, 'rgba(255, 49, 82,0.6)');
					gradient.addColorStop(1, 'rgba(255, 49, 82,0.03)');
					return gradient;
				}
			}
		]
	};

	const openCountOptions = {
		responsive: true,
		plugins: {
			legend: {
				display: false
			},
			title: {
				display: true,
				text: `Unique Mail Open Count - (Total per period: ${totalOpenCount})`
			}
		}
	};

	const openCountTimeData = {
		labels,
		datasets: [
			{
				lineTension: 0.3,
				fill: true,
				data: getOpenCount(),
				borderColor: 'rgb(255, 200, 52)',
				backgroundColor: (context: ScriptableContext<'line'>) => {
					const ctx = context.chart.ctx;
					const gradient = ctx.createLinearGradient(0, 0, 0, 260);
					gradient.addColorStop(0, 'rgba(255, 200, 52,0.6)');
					gradient.addColorStop(1, 'rgba(255, 200, 52,0.03)');
					return gradient;
				}
			}
		]
	};

	return (
		<div className="content-container">
			<Scrollbars autoHide>
				{isLoading ? (
					<LoadingAnimComponent />
				) : isError ? (
					<div className="h-full">
						<div className="flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
							<div className="mx-auto my-auto self-center">
								<div className="min-h-16">
									<DataNotFoundAnimation height={dimensions.height / 1.5} />
								</div>
								<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
									{"Couldn't find the data you're looking for!"}
								</h3>
							</div>
						</div>
					</div>
				) : (
					<main>
						<section className="py-10">
							<div className="mx-auto px-4 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl w-10/12 h-fit transition-all duration-500 ease-in-out">
								<h3 className="dark:text-gray-400 text-lg font-bold mt-2 ml-2 mb-1">
									Latest Statistics
								</h3>
								{isFetching || (
									<div className="grid grid-cols-4 gap-4 px-5 pt-3 mb-3">
										<Card
											latestStats={latestStats.deliveryCount}
											periodicStats={periodicStats}
											period={period}
											perType={PercentageEnum.DELIVERY}
											displayText="Delivery Count"
										/>
										<Card
											latestStats={latestStats.bounceCount}
											periodicStats={periodicStats}
											period={period}
											perType={PercentageEnum.BOUNCE}
											displayText="Bounce Count"
										/>
										<Card
											latestStats={latestStats.complaintCount}
											periodicStats={periodicStats}
											period={period}
											perType={PercentageEnum.COMPLAINT}
											displayText="Complaint Count"
										/>
										<Card
											latestStats={latestStats.openCount}
											periodicStats={periodicStats}
											period={period}
											perType={PercentageEnum.OPEN}
											displayText="Open Count"
										/>

										{/* {showMore && (
										<>
											<CreatedTodoItemsCountCard
												latestStats={latestStats.createdTodoItemsCount}
												periodicStats={periodicStats}
												period={period}
											/>
											<CreatedNotesCountCard
												latestStats={latestStats.createdNotesCount}
												periodicStats={periodicStats}
												period={period}
											/>
										</>
									)} */}
									</div>
								)}

								<div className="flex w-full justify-center">
									<button
										type="button"
										className="btn btn-outline btn-sm btn-primary mb-3"
										onClick={() => setShowMore(!showMore)}
									>
										{showMore ? 'Show less' : 'Show more'}
									</button>
								</div>
							</div>
						</section>

						<section className="px-14">
							<div className="flex w-full justify-end mb-2">
								<div className="inline-flex rounded-md shadow-sm" role="group">
									<div
										className={`py-2 px-4 text-sm font-medium text-gray-900 rounded-l-lg border border-gray-900 dark:border-white transition-all duration-500 cursor-pointer
										${
											period === 'week'
												? 'bg-purple-500 text-white hover:bg-purple-600'
												: 'dark:hover:bg-purple-400 hover:bg-gray-700 hover:text-white dark:text-white dark:hover:text-white'
										}`}
										onClick={() => setPeriod('week')}
									>
										<input
											type="radio"
											name="periodWeek"
											id="week"
											checked={period === 'week'}
											hidden
										/>
										<label className=" cursor-pointer" htmlFor="week">
											Week
										</label>
									</div>

									<div
										className={`py-2 px-4 text-sm font-medium text-gray-900 border-t border-b border-l border-gray-900 dark:border-white transition-all duration-500 cursor-pointer
										${
											period === 'month'
												? 'bg-purple-500 text-white hover:bg-purple-600'
												: 'dark:hover:bg-purple-400 hover:bg-gray-700 hover:text-white dark:text-white dark:hover:text-white'
										}`}
										onClick={() => setPeriod('month')}
									>
										<input
											type="radio"
											name="periodMonth"
											id="month"
											checked={period === 'month'}
											hidden
										/>
										<label className=" cursor-pointer" htmlFor="month">
											Month
										</label>
									</div>

									<div
										className={`py-2 px-4 text-sm font-medium text-gray-900 border-t border-b border-l border-gray-900 dark:border-white transition-all duration-500 cursor-pointer
										${
											period === 'sixMonths'
												? 'bg-purple-400  text-white'
												: 'dark:hover:bg-purple-400 hover:bg-gray-700 hover:text-white dark:text-white dark:hover:text-white'
										}`}
										onClick={() => setPeriod('sixMonths')}
									>
										<input
											type="radio"
											name="periodSixMonths"
											id="sixMonths"
											checked={period === 'sixMonths'}
											hidden
										/>
										<label className=" cursor-pointer" htmlFor="sixMonths">
											6 Months
										</label>
									</div>

									<div
										className={`py-2 px-4 text-sm font-medium text-gray-900 rounded-r-md border border-gray-900 dark:border-white transition-all duration-500 cursor-pointer
										${
											period === 'year'
												? 'bg-purple-400 text-white'
												: 'dark:hover:bg-purple-400 hover:bg-gray-700 hover:text-white dark:text-white dark:hover:text-white'
										}`}
										onClick={() => setPeriod('year')}
									>
										<input
											type="radio"
											name="periodYear"
											id="year"
											checked={period === 'year'}
											onChange={() => setPeriod('year')}
											hidden
										/>
										<label className=" cursor-pointer" htmlFor="year">
											Year
										</label>
									</div>
								</div>
							</div>

							{isFetching ? (
								<LoadingAnimComponent height={300} />
							) : (
								<div className="grid grid-cols-2 r mx-auto w-full">
									<div className="px-4 py-2 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl transition-all duration-500 ease-in-out mb-3">
										<Line
											options={deliveryCountOptions}
											data={deliveryCountData}
										/>
									</div>
									<div className="ml-3 px-4 py-2 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl transition-all duration-500 ease-in-out mb-3">
										<Line options={bounceCountOptions} data={bounceCountData} />
									</div>
									<div className="px-4 py-2 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl transition-all duration-500 ease-in-out mb-3">
										<Line
											options={complaintCountOptions}
											data={complaintCountData}
										/>
									</div>
									<div className="ml-3 px-4 py-2 rounded-xl dark:bg-gray-800 dark:shadow-lg shadow-xl transition-all duration-500 ease-in-out mb-3">
										<Line options={openCountOptions} data={openCountTimeData} />
									</div>
								</div>
							)}
						</section>
					</main>
				)}
			</Scrollbars>
		</div>
	);
}

type PercentageType = { percentage: number; isIncrease: boolean };

enum PercentageEnum {
	DELIVERY = 'delivery',
	OPEN = 'open',
	BOUNCE = 'bounce',
	COMPLAINT = 'complaint'
}

const getCountPercentageDiff = (
	latestStats: number,
	periodicStats: any[],
	period: string,
	perType: PercentageEnum
): PercentageType => {
	try {
		let previousStat = periodicStats[periodicStats.length - 2];

		if (period === 'month') {
			previousStat = periodicStats[periodicStats.length - 8];
		}

		let onePrecentofLatestValue;
		let previousValue;
		let currentValue;

		if (perType === PercentageEnum.DELIVERY) {
			onePrecentofLatestValue = previousStat.deliveryCount / 100;
			previousValue = previousStat.deliveryCount;
			currentValue = latestStats;
		} else if (perType === PercentageEnum.BOUNCE) {
			onePrecentofLatestValue = previousStat.bounceCount / 100;
			previousValue = previousStat.bounceCount;
			currentValue = latestStats;
		} else if (perType === PercentageEnum.COMPLAINT) {
			onePrecentofLatestValue = previousStat.complaintCount / 100;
			previousValue = previousStat.complaintCount;
			currentValue = latestStats;
		} else {
			onePrecentofLatestValue = previousStat.openCount / 100;
			previousValue = previousStat.openCount;
			currentValue = latestStats;
		}

		const percentage = (currentValue - previousValue) / onePrecentofLatestValue;

		const isIncrease = percentage >= 0 ? true : false;

		return {
			percentage: parseFloat(percentage.toFixed(2)),
			isIncrease: isIncrease
		};
	} catch (e) {
		return {
			percentage: 0,
			isIncrease: true
		};
	}
};

type LatestStatsProps = {
	latestStats: number;
	periodicStats: any[];
	period: string;
	percentage?: number;
	isIncrease?: boolean;

	perType: PercentageEnum;

	displayText: string;
};

const Card = ({
	latestStats,
	periodicStats,
	period,
	perType,
	displayText
}: LatestStatsProps) => {
	const [percentageDiff, setPercentageDiff] = useState<PercentageType>(
		getCountPercentageDiff(latestStats, periodicStats, period, perType)
	);

	useEffect(() => {
		setPercentageDiff(
			getCountPercentageDiff(latestStats, periodicStats, period, perType)
		);
	}, [period]);

	return (
		<div className="flex rounded-lg h-full justify-center ">
			<div className="relative flex flex-row h-20 my-auto">
				<div className="mt-0.5">
					<LazyImage photo={users} dimesnions={'70'} loaderDimesnions={70} />
				</div>
				<div className="flex flex-col ml-3 w-fit h-full mt-0.5">
					<h3 className="dark:text-gray-400 text-xs font-bold text-center">
						{displayText}
					</h3>
					<h3 className="dark:text-white text-3xl font-bold text-center">
						{latestStats}
					</h3>

					<div className="flex flex-row w-full justify-center">
						<h3
							className={`text-xs font-bold text-center mt-2 mr-1 ${
								percentageDiff.isIncrease ? 'text-green-600' : 'text-red-600'
							}`}
						>
							{!isNaN(percentageDiff.percentage)
								? `${percentageDiff.isIncrease ? '+' : ''}${
										percentageDiff.percentage
								  }%`
								: 'N/A'}
						</h3>
						<h3 className="dark:text-gray-400 text-xs font-bold text-center mt-2">
							{!isNaN(percentageDiff.percentage)
								? `${percentageDiff.isIncrease ? 'Increase' : 'Decrease'}`
								: ''}
						</h3>
					</div>
				</div>
			</div>
		</div>
	);
};
